// Post-login redirect resolution for the admin login feature.
//
// The captured `redirect` query value is attacker-influenceable (it rides in
// the URL), so it is validated and never trusted: it is honoured only when it
// is an internal path that resolves to a route marked `requiresAuth`. Anything
// absent/empty, an external URL (`http(s)://` or a protocol-relative `//`), or
// an internal path that does not map to an admin route falls back to the
// default Admin_Shell route. This prevents open-redirect abuse of the newly
// exposed admin surface (Requirements 2.2, 2.3, 2.4).

// Default Admin_Shell destination used whenever the redirect cannot be trusted.
export const ADMIN_HOME = { name: 'admin' };

/**
 * Resolve the target location to navigate to after a successful sign-in.
 *
 * @param {unknown} redirect - The originally requested destination (typically
 *   `route.query.redirect`). Only a non-empty string is considered.
 * @param {import('vue-router').Router} router - The router used to resolve the
 *   redirect and inspect the matched route's `meta.requiresAuth`.
 * @param {object} [adminHome=ADMIN_HOME] - The default Admin_Shell route
 *   returned when the redirect is not a trusted internal admin path.
 * @returns {string|object} The original `redirect` string when it is an
 *   internal admin path, otherwise the default Admin_Shell route location.
 */
export function resolvePostLoginTarget(redirect, router, adminHome = ADMIN_HOME) {
  // Absent/empty or non-string values → default shell (Requirement 2.3).
  if (!redirect || typeof redirect !== 'string') {
    return adminHome;
  }

  // External absolute (`http(s)://`) or protocol-relative (`//`) URLs are
  // discarded to prevent open redirects (Requirement 2.4).
  if (/^https?:\/\//i.test(redirect) || redirect.startsWith('//')) {
    return adminHome;
  }

  // Resolve the internal path and honour it only when it maps to a route that
  // requires authentication (Requirement 2.2); otherwise fall back to the
  // default shell (Requirement 2.4).
  let resolved;
  try {
    resolved = router.resolve(redirect);
  } catch (e) {
    // A path the router cannot resolve is treated as untrusted.
    return adminHome;
  }

  const isAdmin = !!resolved
    && Array.isArray(resolved.matched)
    && resolved.matched.some((r) => r.meta && r.meta.requiresAuth);

  return isAdmin ? redirect : adminHome;
}
